import React, { useState, useEffect, useRef, useCallback } from 'react';
import JobCardGrid from 'components/JobCardGrid';
import CareersJobMobileList from 'components/CareersJobMobileList';
import BackgroundImage from 'components/BackgroundImage';
import classNames from 'classnames/bind';
import deviceInfo from 'helpers/deviceInfo';
import { useHistory } from 'react-router-dom';
import canUseDOM from 'helpers/canUseDOM';
import useGreenHouseJobs from '../../hooks/useGreenHouseJobs';
import useNavigation from '../../hooks/useNavigation';
import useBreakpoint from '../../hooks/useBreakpoint';

import styles from './styles.scss';

const cx = classNames.bind(styles);

const isSafari = canUseDOM ? window.safari : false;

const CareersJobList = props => {
  const { data, transporterHeadRef } = props;
  const careersRef = useRef();

  const breakpoint = useBreakpoint();
  const history = useHistory();
  const jobs = useGreenHouseJobs();
  const {
    state: navState,
    setActiveID,
    setNavVisible,
    setNavToggling,
    setNavGradient
  } = useNavigation();

  const [active, setActive] = useState(null);
  const [currentID, setCurrentID] = useState(0);
  const [jobsData, setJobsData] = useState({});
  const [jobLocations, setJobLocations] = useState([]);
  const [clicked, setClicked] = useState(false);
  const [slideList, setSlideList] = useState(false);

  const jobRef = useRef(null);
  const careersDesktopListRef = useRef();
  const careersDesktopAsideRef = useRef();
  const locationsRef = useRef(false);

  const hasBackground = data.backgroundImage
    ? Object.keys(data.backgroundImage).length !== 0
    : false;
  const hasTransporterParent =
    transporterHeadRef && transporterHeadRef.current && transporterHeadRef.current.offsetParent;
  const transporterPageOffset = hasTransporterParent
    ? transporterHeadRef.current.offsetParent.offsetTop
    : 0;

  const isNavInView = navState.visible && slideList;
  const isBelowDesktop = breakpoint === 'mobile' || breakpoint === 'tablet';

  // Note: Removed class 'CareersJobList__aside--with-offset' this seems to work when emulating mobile, but broke on the actual device.
  // Leaving commented code out incase this works on Android or another device we need to account for.
  // Update: Added back the 'CareersJobList__aside--with-offset' class but set the condition to only use this for Android device.
  const asideClasses = cx('CareersJobList__aside', {
    'CareersJobList__aside--with-offset':
      deviceInfo.isAndroid && isBelowDesktop && hasTransporterParent,
    'CareersJobList__aside--nav-open': isBelowDesktop && isNavInView,
    'CareersJobList__aside--nav-close': isBelowDesktop && !isNavInView
  });

  const ListTransport = cx('CareersJobList__desktopList', {
    'CareersJobList__desktopList--with-offset': hasTransporterParent && !isSafari,
    'CareersJobList__desktopList--nav-open': isNavInView,
    'CareersJobList__desktopList--nav-close': !isNavInView,
    'CareersJobList__desktopList--isSafari': deviceInfo.isSafari && !deviceInfo.isIOS
  });

  const jobsPerDepartments = useCallback(
    () =>
      jobs.reduce((res, job) => {
        job.departments.forEach(department => {
          const { name } = department;
          const list = res[name] || [];
          list.push(job);
          res[name] = list;
        });
        return res;
      }, {}),
    [jobs]
  );

  // componentDidMount
  useEffect(() => {
    const jobs = jobsPerDepartments();
    locationsRef.current = Object.keys(jobs);
    const orderedJob = locationsRef.current.map(loc => jobs[loc]);

    setJobsData(orderedJob);
    setActive(locationsRef.current[0]);
    setJobLocations(locationsRef.current.map(s => s.trim()));

    // Cleanup
    return () => {
      const index = locationsRef.current.indexOf(active);
      if (history.location.pathname === '/contact') {
        setActiveID(index);
      } else {
        setActiveID(0);
      }
    };
  }, [jobsPerDepartments]);

  // componentDidUpdate
  useEffect(() => {
    const currentJobs = jobs;
    const jobs = jobsPerDepartments();

    const jobsArr = Object.keys(jobs);

    locationsRef.current = jobsArr.map(s => s.trim());

    if (
      locationsRef.current &&
      locationsRef.current.length &&
      currentJobs &&
      jobs.length !== currentJobs.length
    ) {
      const orderedJob = locationsRef.current.map(loc => jobs[loc]);
      setJobsData(orderedJob);
      setActive(locationsRef.current[0]);
      setJobLocations(locationsRef.current);
      return;
    }

    if (breakpoint === 'largeDesktop' || breakpoint === 'smallDesktop') {
      // get the height of career ul
      const careersDesktopHeight =
        !deviceInfo.isSafari && !deviceInfo.isIOS ? careersDesktopListRef.current.offsetHeight : 0;
      // minus the height of career ul to the height of aside
      careersDesktopAsideRef.current.setAttribute(
        'style',
        `height: calc(100% - ${careersDesktopHeight}px); grid-row: 1 / span  ${jobLocations.length}`
      );
    }
  }, [jobs, breakpoint, clicked, jobsData]);

  function setJobLocation(location, boolean) {
    if (
      active !== location ||
      clicked !== boolean ||
      currentID !== locationsRef.current.indexOf(location)
    ) {
      setActive(location);
      setClicked(boolean);
      setCurrentID(locationsRef.current.indexOf(location));
    }
  }

  function setFixedNavigation(isFixed, isToggleFixed, gradient) {
    setNavToggling(isToggleFixed);
    setNavVisible(isFixed);
    if (gradient !== null || gradient !== undefined) setNavGradient(gradient);
  }

  // for a11y sequential tabbing
  function careersSequentialTabbing() {
    const careersTab = document.querySelectorAll('.careers-tab');
    const navList = document.querySelectorAll(
      '.src-sites-candt-components-CareersJobList-locationButton'
    );
    const jobList = document.querySelectorAll('.careers-job-link.careers-tab');
    const arr = [];

    // add data-prev and data-next attributes on location list
    [...navList].map((item, i) => {
      const locationList = document.querySelectorAll(`.careers-job-link.location-${i}`);
      arr[i] = [...locationList];

      item.setAttribute('data-next', `.location-${i}.careers-tab-0`);
      if (i !== 0) {
        item.setAttribute('data-prev', `.location-${i - 1}.careers-tab-${arr[i - 1].length - 1}`);
      }
    });

    // add data-prev and data-next attributes on fist and last job list
    arr.map((item, i) => {
      item[0].setAttribute('data-prev', `.location-${i}`);
      if (i !== arr.length - 1) {
        item[item.length - 1].setAttribute('data-next', `.location-${i + 1}`);
      }
    });

    // set the prev and next focus based on data-attribute
    const locationJob = [...careersTab, ...jobList];
    locationJob.map(item =>
      item.addEventListener('keydown', e => {
        const prev = e.target.dataset.prev || false;
        const next = e.target.dataset.next || false;
        const keyCode = e.keyCode || e.which;
        if (keyCode === 9) {
          if (e.shiftKey) {
            if (prev) {
              e.preventDefault();
              document.querySelector(prev).focus();
            }
          } else if (next) {
            e.preventDefault();
            document.querySelector(next).focus();
          }
        }
        // focus on enter
        if (keyCode === 13) {
          if (next) {
            e.preventDefault();
            document.querySelector(next).focus();
          }
        }
      })
    );
  }

  useEffect(() => {
    if (canUseDOM) {
      const timeout = setTimeout(() => {
        careersSequentialTabbing();
      }, 500);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, []);

  return (
    <section ref={careersRef} className={styles.CareersJobList}>
      {hasBackground ? (
        <BackgroundImage
          src={data.backgroundImage[0].url}
          className={styles.CareersJobList__background}
        />
      ) : null}
      <div className={`wrapper grid-container ${styles.CareersJobList__container}`}>
        <aside className={asideClasses} ref={careersDesktopAsideRef}>
          {breakpoint === 'largeDesktop' || breakpoint === 'smallDesktop' ? (
            <ul className={ListTransport} ref={careersDesktopListRef}>
              {locationsRef.current &&
                locationsRef.current.map((location, i) => (
                  <li
                    key={i}
                    className={`${styles.CareersJobList__desktopList__item} ${
                      styles.CareersJobList__desktopList__item
                    }${active === location ? '--active-link' : ''}`}>
                    <button
                      type="button"
                      ref={jobRef}
                      className={`${styles.locationButton} careers-tab location-${i}`}
                      onFocus={() => setJobLocation(location, true)}
                      onClick={() => setJobLocation(location, true)}>
                      {location}
                    </button>
                  </li>
                ))}
            </ul>
          ) : (
            jobLocations.length > 0 &&
            Object.values(jobsData).length > 0 && (
              <CareersJobMobileList
                jobLocations={jobLocations}
                active={active}
                className={styles.CareersJobList}
                btnClass={styles.locationButton}
                setJobLocation={(loc, bool) => setJobLocation(loc, bool)}
                breakpoint={breakpoint}
                clicked={clicked}
                transporterPageOffset={transporterPageOffset}
                activeID={navState.activeID}
              />
            )
          )}
        </aside>
        {jobLocations.length > 0 && Object.values(jobsData).length > 0 && (
          <JobCardGrid
            setJobLocation={(loc, bool) => setJobLocation(loc, bool)}
            location={active}
            jobsData={jobsData}
            jobLocations={jobLocations}
            setClick={boolean => setClicked(boolean)}
            setSlide={bool => setSlideList(bool)}
            clicked={clicked}
            initLocations={locationsRef.current}
            currentID={currentID}
            careersRef={careersRef}
            setNavVisible={setNavVisible}
            setFixedNavigation={(isFixed, isToggleFixed) =>
              setFixedNavigation(isFixed, isToggleFixed)
            }
            navVisibile={navState.visible}
            navToggling={navState.toggling}
            setActiveID={setActiveID}
            transporterPageOffset={transporterPageOffset}
          />
        )}
      </div>
    </section>
  );
};

export default CareersJobList;
